// Mochi's "what should I do?" picker — rate limiting, highlight lifecycle,
// and the special-toast-on-completion-within-30min logic all live here so
// the React components stay declarative.
import api from "@/lib/api";

const LS_KEY_LAST_TAP = "mochi-pick-last-tap";
const LS_KEY_CURRENT = "mochi-pick-current";

// Per spec: rate limit the button to once every 5 minutes.
const RATE_LIMIT_MS = 5 * 60 * 1000;
// 10 minutes — golden highlight fades quietly after this.
export const HIGHLIGHT_TTL_MS = 10 * 60 * 1000;
// 30 minutes — completing the suggested task within this window fires the
// special "that's exactly what i thought you'd do 🍡" toast.
export const SPECIAL_TOAST_WINDOW_MS = 30 * 60 * 1000;

export function getLastTapAt() {
    try {
        const raw = window.localStorage.getItem(LS_KEY_LAST_TAP);
        return raw ? parseInt(raw, 10) || 0 : 0;
    } catch { return 0; }
}

export function isRateLimited() {
    const last = getLastTapAt();
    if (!last) return false;
    return Date.now() - last < RATE_LIMIT_MS;
}

// Returns seconds until the user can tap again. 0 when ready.
export function secondsUntilNextTap() {
    const last = getLastTapAt();
    if (!last) return 0;
    const remaining = RATE_LIMIT_MS - (Date.now() - last);
    return remaining > 0 ? Math.ceil(remaining / 1000) : 0;
}

function persistCurrent(pick) {
    try {
        window.localStorage.setItem(LS_KEY_CURRENT, JSON.stringify(pick));
    } catch (e) { console.warn("mochi pick persist failed", e); }
}

function readCurrent() {
    try {
        const raw = window.localStorage.getItem(LS_KEY_CURRENT);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}

// Surface the live pick to consumers (e.g. ItemRow highlight check). Returns
// null if no pick is set or the highlight has expired (10 min TTL).
export function getCurrentPick() {
    const p = readCurrent();
    if (!p) return null;
    if (Date.now() - p.created_at > HIGHLIGHT_TTL_MS) return null;
    return p;
}

export function clearCurrentPick() {
    try { window.localStorage.removeItem(LS_KEY_CURRENT); } catch (e) { console.warn("mochi pick clear failed", e); }
}

// Fires the /api/mochi/suggest call with an 8-second client-side timeout per
// spec. Returns the parsed response, or a synthetic fallback object so the
// caller doesn't have to catch.
export async function requestSuggestion(energy) {
    // Stamp the tap timestamp BEFORE the call — this guards against rapid
    // double-taps from racing past the rate limit.
    try {
        window.localStorage.setItem(LS_KEY_LAST_TAP, String(Date.now()));
    } catch (e) { console.warn("mochi pick rate-limit stamp failed", e); }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);
    try {
        const res = await api.post("/mochi/suggest", { energy }, { signal: controller.signal });
        clearTimeout(timeoutId);
        const data = res.data;
        if (data?.task_id) {
            const pick = {
                task_id: data.task_id,
                task_title: data.task_title,
                message: data.message,
                created_at: Date.now(),
            };
            persistCurrent(pick);
        } else {
            clearCurrentPick();
        }
        return data;
    } catch (err) {
        clearTimeout(timeoutId);
        // Soft fallback line — spec says "no error message".
        console.warn("mochi suggest call failed — serving soft fallback", err);
        clearCurrentPick();
        return {
            task_id: null,
            task_title: null,
            message: "you know what you need better than i do. trust that 🌸",
        };
    }
}

// Called by the toggle-task handler. Returns "special" if the task that just
// completed was the active pick AND it landed within the 30-min window;
// otherwise null. Either way, the pick is cleared so we don't double-fire.
export function consumeIfMatchesCompletion(taskId) {
    const pick = readCurrent();
    if (!pick || pick.task_id !== taskId) return null;
    const within = Date.now() - pick.created_at <= SPECIAL_TOAST_WINDOW_MS;
    clearCurrentPick();
    return within ? "special" : "expired";
}
